import {
  getAccessToken,
  getRefreshToken,
  getTenantId,
  setLocalAccessToken,
  setLocalRefreshToken,
} from "@/lib/storeManager";
import { jwtDecode } from "jwt-decode";
import axios from "axios";

/** Access token lifetime on the backend (15 minutes). */
export const ACCESS_TOKEN_LIFETIME_MS = 15 * 60 * 1000;

/** Refresh proactively every 14 minutes, before access token expires. */
export const TOKEN_REFRESH_INTERVAL_MS = 14 * 60 * 1000;

const REFRESH_BUFFER_MS = 60_000;

export interface TokenPair {
  access: string;
  refresh?: string;
}

let inFlightRefresh: Promise<TokenPair> | null = null;
let refreshIntervalId: ReturnType<typeof setInterval> | null = null;

export async function performTokenRefresh(): Promise<TokenPair> {
  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  const refresh = getRefreshToken();
  if (!refresh) {
    throw new Error("No refresh token available");
  }

  const tenantId = getTenantId();
  inFlightRefresh = axios
    .post<TokenPair>(
      `${import.meta.env.VITE_API_BASE_URL}/auth/token-refresh/`,
      { refresh },
      {
        headers: {
          "Content-Type": "application/json",
          ...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
        },
      }
    )
    .then(response => {
      const { access, refresh: newRefresh } = response.data;
      if (!access) {
        throw new Error("Token refresh response missing access token");
      }
      setLocalAccessToken(access);
      if (newRefresh) {
        setLocalRefreshToken(newRefresh);
      }
      return response.data;
    })
    .finally(() => {
      inFlightRefresh = null;
    });

  return inFlightRefresh;
}

function shouldRefreshAccessTokenNow(): boolean {
  const access = getAccessToken();
  if (!access) {
    return true;
  }
  try {
    const { exp } = jwtDecode<{ exp: number }>(access);
    return exp * 1000 - Date.now() < REFRESH_BUFFER_MS;
  } catch {
    return true;
  }
}

export type TokenRefreshHandlers = {
  onSuccess?: (tokens: TokenPair) => void;
  onFailure?: () => void;
};

export function startProactiveTokenRefresh(
  handlers?: TokenRefreshHandlers
): void {
  stopProactiveTokenRefresh();

  if (!getRefreshToken()) {
    return;
  }

  const runRefresh = async () => {
    try {
      const tokens = await performTokenRefresh();
      handlers?.onSuccess?.(tokens);
    } catch {
      handlers?.onFailure?.();
    }
  };

  if (shouldRefreshAccessTokenNow()) {
    void runRefresh();
  }

  refreshIntervalId = setInterval(runRefresh, TOKEN_REFRESH_INTERVAL_MS);
}

export function stopProactiveTokenRefresh(): void {
  if (refreshIntervalId !== null) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
}
